import React from 'react';
import { X, Settings } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Label } from './Label';
import {
  WorkflowNode,
  getNodeDefinition,
  WorkflowNodeConfig as NodeConfig,
} from '../helpers/workflowTypes';
import styles from './WorkflowNodeConfig.module.css';

interface WorkflowNodeConfigProps {
  node: WorkflowNode;
  onConfigChange: (config: NodeConfig) => void;
  onNameChange: (name: string) => void;
  onClose: () => void;
}

export const WorkflowNodeConfig: React.FC<WorkflowNodeConfigProps> = ({
  node,
  onConfigChange,
  onNameChange,
  onClose,
}) => {
  const definition = getNodeDefinition(node.type);

  const handleFieldChange = (key: string, value: any) => {
    onConfigChange({
      ...node.config,
      [key]: value,
    });
  };

  const renderField = (
    field: typeof definition extends { configFields: infer F }
      ? F extends Array<infer T>
        ? T
        : never
      : never,
  ) => {
    if (!field) return null;
    const value = node.config[field.key as keyof NodeConfig];

    switch (field.type) {
      case 'text':
        return (
          <Input
            value={(value as string) || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
          />
        );

      case 'code':
        return (
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={8}
            className={styles.codeInput}
            style={{ fontFamily: 'monospace' }}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={(value as number) || field.defaultValue || ''}
            onChange={(e) => handleFieldChange(field.key, parseInt(e.target.value, 10))}
            placeholder={field.placeholder}
          />
        );

      case 'select':
        return (
          <Select
            value={(value as string) || field.defaultValue || ''}
            onValueChange={(v) => handleFieldChange(field.key, v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'json':
        return (
          <Textarea
            value={typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleFieldChange(field.key, parsed);
              } catch {
                handleFieldChange(field.key, e.target.value);
              }
            }}
            placeholder={field.placeholder}
            rows={4}
            className={styles.codeInput}
            style={{ fontFamily: 'monospace' }}
          />
        );

      case 'checkbox':
        return (
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={value === true || value === 'true' || (value === undefined && field.defaultValue === true)}
              onChange={(e) => handleFieldChange(field.key, e.target.checked)}
              className={styles.checkboxInput}
            />
            <span>{field.helpText || field.label}</span>
          </label>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Settings size={16} />
          <span>Configure Node</span>
        </div>
        <Button size="icon-sm" variant="ghost" onClick={onClose}>
          <X size={16} />
        </Button>
      </div>

      <div className={styles.content}>
        {/* Node name */}
        <div className={styles.field}>
          <Label>Node Name</Label>
          <Input
            value={node.name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Node name..."
          />
        </div>

        {/* Node type info */}
        <div className={styles.typeInfo}>
          <span className={styles.typeBadge}>{definition?.category}</span>
          <span className={styles.typeDescription}>{definition?.description}</span>
        </div>

        {/* Config fields */}
        {definition?.configFields.map((field) => (
          <div key={field.key} className={styles.field}>
            <Label>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </Label>
            {renderField(field as any)}
          </div>
        ))}

        {/* No config message */}
        {(!definition?.configFields || definition.configFields.length === 0) && (
          <div className={styles.noConfig}>
            <p>This node has no configuration options.</p>
          </div>
        )}
      </div>
    </div>
  );
};
